import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { nanoid } from "nanoid";
import { isNil } from "lodash";

import { Box, CircularProgress, Link, Stack } from "@mui/material";
import { Create } from "@mui/icons-material";

import {
  CoverageScheduledPersonalProperty,
  PersonalProperty,
  personalPropertyTypeGraphQLToViewMap,
  useGetValuableItemProtectionPriceLazyQuery,
} from "@covertree/new-business-graphql";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { theme } from "@covertree/theme";
import { FormToggleCard, HelpText, Modal } from "@covertree/ui";
import { formatCurrency } from "@covertree/utils";

import { SchedulePersonalPropertyModalContent } from "./SchedulePersonalPropertyModalContent";

interface ScheduledPersonalPropertyCardProps {
  scheduledPersonalPropertyDetails: Omit<
    CoverageScheduledPersonalProperty,
    "valueIsHidden"
  > & {
    description: string;
    image: string;
    name: string;
    tooltip: string;
    valueIsCustomizable: boolean;
  };
  readonly?: boolean;
}

export type PersonalPropertyWithPrice = PersonalProperty & { price?: number };

export enum ModalTitleForValuables {
  Initial = "What type of items would you like to protect?",
  NameAndValue = "What is the item’s name and value?",
  ValuableAdded = "Valuable Added",
  ValuableRemoved = "Valuable Removed",
  ValuableItems = "Valuable items",
}

export function ScheduledPersonalPropertyCard({
  scheduledPersonalPropertyDetails: {
    description,
    image,
    name,
    tooltip,
    valueIsCustomizable,
  },
  readonly = false,
}: ScheduledPersonalPropertyCardProps) {
  const { policyLocator } = useNewBusinessSelector(selectPolicy)!;
  const [getValuableItemProtectionPrice] =
    useGetValuableItemProtectionPriceLazyQuery();

  const { control, trigger, getValues, setValue, watch } = useFormContext<{
    scheduledPersonalProperty: (PersonalPropertyWithPrice & { id: string })[];
  }>();
  const [valuesOnModalOpen, setValuesOnModalOpen] = useState<
    (PersonalPropertyWithPrice & { id: string })[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isAddedItemsPricingLoading, setIsItemsPricingLoading] =
    useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<ModalTitleForValuables>(
    ModalTitleForValuables.Initial
  );

  const { append, update, remove } = useFieldArray({
    control,
    name: "scheduledPersonalProperty",
  });

  const fields = watch("scheduledPersonalProperty");

  // This useEffect should load initial prices for SPPs, if policy was continued
  useEffect(() => {
    (async () => {
      if (fields.length > 0 && fields.some(({ price }) => isNil(price))) {
        setIsItemsPricingLoading(true);
        await Promise.all(
          fields.map(async (item, idx) => {
            if (item.price) return;

            const price = await getValuableItemProtectionPrice({
              variables: {
                personalizedValuableItem: item.type,
                personalizedValuableName: item.description,
                personalizedValuableValue: item.value,
                policyLocator,
              },
            });

            update(idx, {
              ...item,
              price: price.data?.getValuableItemProtectionPrice,
            });
          })
        );
        setIsItemsPricingLoading(false);
      }
    })();
  }, [fields, policyLocator, update]);

  const addOrUpdateItem = useCallback(
    (
      personalPropertyToAddOrUpdate: PersonalPropertyWithPrice & { id: string }
    ) => {
      const { id, fieldGroupLocator } = personalPropertyToAddOrUpdate;

      if (!id && !fieldGroupLocator) {
        append({
          ...personalPropertyToAddOrUpdate,
          id: nanoid(),
        });
        return;
      }

      const idx = getValues("scheduledPersonalProperty").findIndex(
        ({ fieldGroupLocator, id }) =>
          (!!personalPropertyToAddOrUpdate.fieldGroupLocator &&
            personalPropertyToAddOrUpdate.fieldGroupLocator ===
              fieldGroupLocator) ||
          (!isNil(id) && id === personalPropertyToAddOrUpdate.id)
      );

      if (idx === -1) {
        append(personalPropertyToAddOrUpdate);
      } else {
        update(idx, personalPropertyToAddOrUpdate);
      }
    },
    [append, getValues, update]
  );

  const handleDeleteItem = useCallback(
    (personalPropertyToDelete: PersonalProperty & { id: string }) => {
      const values = getValues(
        "scheduledPersonalProperty"
      ) as (PersonalProperty & { id: string })[];

      if (personalPropertyToDelete.fieldGroupLocator) {
        const index = values.findIndex(
          (item) =>
            item.fieldGroupLocator ===
            personalPropertyToDelete.fieldGroupLocator
        );
        if (index > -1) {
          remove(index);
          return values.length - 1;
        }
      }

      if (personalPropertyToDelete.id) {
        const index = values.findIndex(
          (item) => item.id === personalPropertyToDelete.id
        );
        if (index > -1) {
          remove(index);
          return values.length - 1;
        }
      }

      return values.length;
    },
    [remove, getValues]
  );

  const handleOpenModal = useCallback(async () => {
    setValuesOnModalOpen(
      structuredClone(getValues("scheduledPersonalProperty"))
    );
    await trigger("scheduledPersonalProperty");
    setIsModalOpen(true);
  }, []);

  const handleCloseModalWithSave = useCallback(() => {
    setIsModalOpen(false);
    if (getValues("scheduledPersonalProperty").length > 0) {
      setModalTitle(ModalTitleForValuables.ValuableItems);
    } else {
      setModalTitle(ModalTitleForValuables.Initial);
    }
  }, [setIsModalOpen]);

  const handleCloseModal = useCallback(() => {
    setValue("scheduledPersonalProperty", valuesOnModalOpen);
    handleCloseModalWithSave();
  }, [valuesOnModalOpen, setIsModalOpen]);

  const currentPriceOfAllItems = useMemo(() => {
    return fields.reduce((acc, { price }) => acc + (price ?? 0), 0);
  }, [fields]);

  return (
    <FormToggleCard
      title={name}
      image={image}
      tooltip={tooltip}
      actionText={
        fields.length > 0 && currentPriceOfAllItems > 0
          ? `+ ${formatCurrency(currentPriceOfAllItems / 12, 2)}/mo`
          : undefined
      }
      actionSubText={
        fields.length > 0 && currentPriceOfAllItems > 0
          ? `(${formatCurrency(currentPriceOfAllItems, 2)}/year)`
          : undefined
      }
      subheader={description}
      subheaderVariant="grey"
      onToggle={
        fields.length > 0
          ? () => {
              remove();
            }
          : handleOpenModal
      }
      checked={fields.length > 0}
      checkedCustomStyles={{
        backgroundColor: theme.palette.secondary.contrastText,
      }}
      customizable={valueIsCustomizable}
      actions={
        fields.length > 0 ? (
          <Stack sx={{ width: "100%" }} mb={3} mt={2} spacing={1}>
            {fields.map((item) => (
              <Box key={item.id ?? item.fieldGroupLocator} role="listitem">
                <HelpText
                  text={`${personalPropertyTypeGraphQLToViewMap[item.type]}:`}
                  spanText={formatCurrency(item.value)}
                />
              </Box>
            ))}
          </Stack>
        ) : undefined
      }
      content={
        <>
          {!readonly && (
            <Link
              component="button"
              type="button"
              onClick={handleOpenModal}
              color="secondary"
              variant="body2"
              underline="always">
              <Box
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  "& svg": {
                    marginRight: theme.spacing(1),
                  },
                })}>
                <Create fontSize="inherit" />
                {fields.length === 0 ? "Add" : "Edit"}
              </Box>
            </Link>
          )}
          <Modal
            maxWidth="sm"
            title={modalTitle}
            open={isModalOpen}
            onClose={handleCloseModal}>
            {isAddedItemsPricingLoading ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={(theme) => ({
                  width: theme.spacing(10),
                  height: theme.spacing(10),
                })}>
                <CircularProgress color="primary" />
              </Box>
            ) : (
              <SchedulePersonalPropertyModalContent
                addOrUpdateItem={addOrUpdateItem}
                closeModal={handleCloseModalWithSave}
                currentPriceOfAllItems={currentPriceOfAllItems}
                deleteItem={handleDeleteItem}
                fields={fields}
                updateModalTitle={setModalTitle}
              />
            )}
          </Modal>
        </>
      }
    />
  );
}
